import { FlatRecord } from 'flatrecord/geojson'
import GeoJSON from 'geojson'
import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Navaid, Waypoint } from '@/domain/models/Waypoint'
import { Coordinates } from '@/domain/protocols/Coordinates'
import { Result } from '@/domain/protocols/Result'

import { INavaidRepository } from './INavaidRepository'
import { mapFeatureToNavaid } from './NavaidFlatRecordMapper'

import { I18n } from '@/i18n/I18n'
import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { loadDatasetBytes } from '@/infra/datasetCache/loadDatasetBytes'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import type { ILogger } from '@/infra/logger/ILogger'
import { nmDistance } from '@/utils/coordinates'
import { removeAccents } from '@/utils/string'
import { isSet } from '@/utils/validation'

const LOG_TAG = 'NavaidRepository'
const DEFAULT_TEXT_SEARCH_MAX = 50

@injectable()
export class NavaidRepository implements INavaidRepository {
  private navaids: Navaid[] = []
  private lastLoadedAt: Date | null = null

  constructor(
    @inject(InjectionTokens.HttpClient) private readonly httpClient: IHttpClient,
    @inject(InjectionTokens.Logger) private readonly logger: ILogger,
    @inject(InjectionTokens.DatasetCache) private readonly datasetCache: DatasetCache,
    @inject(InjectionTokens.I18n) private readonly i18n: I18n
  ) {}

  async init(): Promise<Result<void>> {
    return this.load()
  }

  async refresh(): Promise<Result<void>> {
    return this.load()
  }

  getAllNavaids(): Navaid[] {
    return this.navaids.map((navaid) => navaid.clone())
  }

  searchByProximity(center: Coordinates, radius: number, max?: number): Navaid[] {
    const items = this.navaids
      .map((navaid) => ({ navaid, distance: nmDistance(center, navaid.coordinates) }))
      .filter((item) => item.distance <= radius)
      .sort((a, b) => a.distance - b.distance)
      .map((item) => item.navaid.clone())

    return max ? items.slice(0, max) : items
  }

  searchByInfo(info: string, max: number = DEFAULT_TEXT_SEARCH_MAX): Navaid[] {
    const normalizedInfo = removeAccents(info ?? '')
      .toLowerCase()
      .trim()
    if (!normalizedInfo) return []

    const scoreNavaid = (navaid: Navaid): number | null => {
      const code = navaid.code?.toLowerCase() ?? null
      const name = navaid.name ? removeAccents(navaid.name.toLowerCase()).split(',')[0].trim() : null

      if (code === normalizedInfo) return 3
      if (name && (name === normalizedInfo || name.includes(normalizedInfo))) return 2
      if (code?.startsWith(normalizedInfo) || name?.startsWith(normalizedInfo)) return 1
      return null
    }

    return this.navaids
      .map((navaid) => ({ navaid, score: scoreNavaid(navaid) }))
      .filter((item): item is { navaid: Navaid; score: number } => item.score !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, max)
      .map((item) => item.navaid.clone())
  }

  getExactMatchWaypoint(waypoint: Waypoint): Navaid | null {
    const byCode = isSet(waypoint.code)
      ? this.navaids.filter((navaid) => isSet(navaid.code) && navaid.code === waypoint.code)
      : []

    if (byCode.length === 1) return byCode[0].clone()

    const pool = byCode.length > 1 ? byCode : this.navaids
    const byName =
      isSet(waypoint.name) && pool.length > 0
        ? pool.filter((navaid) => isSet(navaid.name) && navaid.name === waypoint.name)
        : []

    if (byName.length === 1) return byName[0].clone()
    if (byName.length > 1) return this.closestTo(waypoint.coordinates, byName)?.clone() ?? null

    return null
  }

  getLastLoadedAt(): Date | null {
    return this.lastLoadedAt
  }

  private getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_CDN_BASEURL}/bra/navaids.frb`
  }

  private async load(): Promise<Result<void>> {
    try {
      // Cache-first: dentro do soft TTL isto resolve sem tocar a rede, que e o ganho do reload.
      const loaded = await loadDatasetBytes(
        this.httpClient,
        this.datasetCache,
        'navaids',
        this.getRemoteUrl(),
        this.logger
      )

      if (!loaded) {
        this.logger.warn(LOG_TAG, 'Could not fetch navaid dataset')
        return Result.fail('Could not fetch navaid dataset')
      }

      const fr = await FlatRecord.open(loaded.bytes)
      if (fr.preload) await fr.preload()
      const features = await fr.loadFeatures()

      const language = this.i18n.getLanguage()
      const navaids = features
        .map((feature) => mapFeatureToNavaid(feature as GeoJSON.Feature, language))
        .filter((navaid): navaid is Navaid => navaid !== null)

      // Só substitui a base se o parse rendeu algo — preserva o cache anterior em download ruim.
      if (navaids.length === 0) {
        this.logger.warn(LOG_TAG, 'Navaid dataset parsed to zero features; keeping previous data')
        return Result.fail('Empty navaid dataset')
      }

      this.navaids = navaids
      this.lastLoadedAt = new Date()
      this.logger.info(LOG_TAG, `Loaded ${navaids.length} navaids from FlatRecord`)
      return Result.ok()
    } catch (error: any) {
      this.logger.error(LOG_TAG, `Could not load navaid dataset: ${error?.message ?? error}`)
      return Result.fail(error)
    }
  }

  private closestTo(coordinates: Coordinates, navaids: Navaid[]): Navaid | null {
    let closest: Navaid | null = null
    let closestDistance = Number.MAX_VALUE

    for (const navaid of navaids) {
      if (!navaid.coordinates) continue
      const distance = nmDistance(navaid.coordinates, coordinates)
      if (distance < closestDistance) {
        closestDistance = distance
        closest = navaid
      }
    }

    return closest
  }
}
